<template>
  <Transition name="slide-fadeUp" appear>
    <div v-if="visible" class="modal-center">
      <div class="modal-content">
        <div class="modal-header">
          <span class="modal-header-txt">{{$t('program_generation_progress')}}</span>
          <span class="modal-header-description">{{ getStatusDescription }}</span>
        </div>

        <div class="modal-body">
          <b-progress
              :height="'20px'"
              class="mb-4"
              :value="calculateProgress"
              :max="100"
              show-progress
              :variant="progressVariant"
              animated>
          </b-progress>

          <div class="progress-steps">
            <div class="step" :class="{ 'active': isStepActive('structure_creation'), 'completed': isStepCompleted('structure_creation') }">
              <div class="step-icon">
                <img src="@/assets/images/structure.svg" alt="" />
              </div>
              <div class="step-content">
                <div class="step-title">{{$t('structure_creation')}}</div>
                <div class="step-description">{{ getStepDescription('structure_creation') }}</div>
              </div>
            </div>

            <div class="step" :class="{ 'active': isStepActive('questions_creation'), 'completed': isStepCompleted('questions_creation') }">
              <div class="step-icon">
                <img src="@/assets/images/clipboard.svg" alt="" />
              </div>
              <div class="step-content">
                <div class="step-title">{{$t('content_generation')}}</div>
                <div class="step-description">{{ getStepDescription('questions_creation') }}</div>
              </div>
            </div>

              <div v-if='genStatus?.metadata?.generateVideos !== "false"' class="step" :class="{ 'active': isStepActive('video'), 'completed': isStepCompleted('video') }">
              <div class="step-icon">
                <img src="@/assets/images/videoOutlineWhite.svg" alt="" />
              </div>
              <div class="step-content">
                <div class="step-title">{{$t('video_creation')}}</div>
                <div class="step-description">{{ getStepDescription('video') }}</div>
              </div>
            </div>

            <div class="step" :class="{ 'active': isStepActive('completion'), 'completed': isStepCompleted('completion') }">
              <div class="step-icon">
                <img src="@/assets/images/tickWhite.svg" alt="" />
              </div>
              <div class="step-content">
                <div class="step-title">{{$t('reports.completion')}}</div>
                <div class="step-description">{{ getStepDescription('completion') }}</div>
              </div>
            </div>
          </div>

          <div v-if="currentAction" class="current-action">
            <div class="pulse-dot"></div>
            <span>{{ currentAction }}</span>
          </div>

          <div v-if="genStatus?.error" class="error-message">
            <i class="fas fa-exclamation-triangle"></i>
            <span>{{ genStatus.error }}</span>
            <button class="retry-btn" @click="$emit('retry')">{{ $t("retry") }}</button>
          </div>
        </div>

        <div @click="onHide" class="close-btn">
          <img class="cross" src="../../assets/images/cross.svg" alt="Close" />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { BProgress } from "bootstrap-vue-3";

export default {
  components: { BProgress },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    onHide: {
      type: Function,
      default: () => {}
    },
    genStatus: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    calculateProgress() {
      const { status, queueStep } = this.genStatus || {};

      if (status === 'completed') {
        return 100;
      } else if (status === 'failed') {
        return this.getFailedProgress();
      }

      switch (queueStep) {
        case undefined:
        case null:
        case "pending":
          return 5;
        case "structure_creation_in_progress":
          return 15;
        case "structure_complete":
          return 30;
        case "questions_creation_in_progress":
          return 50;
        case "pending_video":
          return 65;
        case "processing_video":
          return 75;
        case "pending_upload":
          return 90;
        case "complete":
          return 100;
        default:
          return 0;
      }
    },

    progressVariant() {
      if (this.genStatus?.status === 'failed') {
        return 'danger';
      } else if (this.genStatus?.status === 'completed') {
        return 'success';
      } else {
        return 'primary';
      }
    },

    getStatusDescription() {
      const { status, queueStep } = this.genStatus || {};

      if (status === 'completed') {
        return this.$t("your_program_has_been_successfully_generated_and_is_ready_to_use")
      } else if (status === 'failed') {
        return this.$t("there_was_an_issue_during_program_generation");
      } else if (status === 'in_progress') {
        return this.$t("your_program_is_actively_being_generated");
      } else if (status === 'pending') {
        return this.$t("your_program_is_queued_for_generation");
      }

      return this.$t("preparing_to_generate_your_custom_program");
    },

    currentAction() {
      const { queueStep, metadata } = this.genStatus || {};

      if (queueStep === 'questions_creation_in_progress' && metadata?.currentModuleIndex !== undefined) {
        return `${this.$t("generating_content_for_module")} ${metadata.currentModuleIndex + 1}, ${this.$t("submodule")} ${metadata.currentSubmoduleIndex + 1}`;
      } else if (queueStep === 'structure_creation_in_progress') {
        return this.$t("creating_program_structure_based_on_your_requirements");
      } else if (queueStep === 'processing_video' && metadata?.generateVideos !== "false") {
        return this.$t("processing_ai_generated_video_content");
      } else if (queueStep === 'pending_upload') {
        return this.$t("uploading_and_finalizing_your_program_content");
      }

      return null;
    }
  },

  methods: {
    getFailedProgress() {
      const { queueStep } = this.genStatus || {};

      switch (queueStep) {
        case "structure_creation_in_progress":
          return 15;
        case "structure_complete":
          return 30;
        case "questions_creation_in_progress":
          return 50;
        case "pending_video":
          return 65;
        case "processing_video":
          return 75;
        case "pending_upload":
          return 90;
        default:
          return 5;
      }
    },

    isStepActive(step) {
      const { queueStep } = this.genStatus || {};

      switch (step) {
        case 'structure_creation':
          return queueStep === 'structure_creation_in_progress';
        case 'questions_creation':
          return queueStep === 'questions_creation_in_progress';
        case 'video':
          return queueStep === 'pending_video' || queueStep === 'processing_video' || queueStep === 'pending_upload';
        case 'completion':
          return queueStep === 'complete' && this.genStatus?.status === 'completed';
        default:
          return false;
      }
    },

    isStepCompleted(step) {
      const { queueStep, status } = this.genStatus || {};

      if (status === 'completed') {
        return true;
      }

      switch (step) {
        case 'structure_creation':
          return queueStep === 'structure_complete' ||
              queueStep === 'questions_creation_in_progress' ||
              queueStep === 'pending_video' ||
              queueStep === 'processing_video' ||
              queueStep === 'pending_upload' ||
              queueStep === 'complete';
        case 'questions_creation':
          return queueStep === 'pending_video' ||
              queueStep === 'processing_video' ||
              queueStep === 'pending_upload' ||
              queueStep === 'complete';
        case 'video':
          return queueStep === 'pending_upload' ||
              queueStep === 'complete';
        case 'completion':
          return queueStep === 'complete' && status === 'completed';
        default:
          return false;
      }
    },

    getStepDescription(step) {
      const { queueStep, status } = this.genStatus || {};

      if (status === 'failed') {
        if (this.isStepActive(step)) {
          return this.$t("failed_at_this_step");
        }
      }

      switch (step) {
        case 'structure_creation':
          if (this.isStepCompleted(step)) return this.$t("program_structure_created");
          if (this.isStepActive(step)) return this.$t("creating_program_structure");
          return this.$t("waiting_to_start");

        case 'questions_creation':
          if (this.isStepCompleted(step)) return this.$t("educational_content_created");
          if (this.isStepActive(step)) return this.$t("generating_educational_content");
          return this.$t("waiting_for_structure_completion");

        case 'video':
          if (this.isStepCompleted(step)) return this.$t("videos_created_and_processed");
          if (queueStep === 'pending_video') return this.$t("preparing_to_generate_videos");
          if (queueStep === 'processing_video') return this.$t("processing_ai_videos");
          if (queueStep === 'pending_upload') return this.$t("preparing_to_upload_videos");
          return this.$t("waiting_for_content_generation");

        case 'completion':
          if (status === 'completed') return this.$t("program_successfully_generated");
          return this.$t("your_program_has_been_successfully_generated_and_is_ready_to_use");
      }
    }
  }
};
</script>

<style scoped>
.modal-content {
  position: relative;
  background-color: white;
  width: auto;
  height: auto;
  border-radius: 12px;
  padding: 24px !important;
  overflow-y: auto;
  max-height: calc(100vh - 48px);
  min-width: 550px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.modal-body {
  padding: 12px 0;
}

.modal-content::-webkit-scrollbar {
  width: 12px;
}

.modal-content::-webkit-scrollbar-track {
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  background-color: #FFF;
}

.modal-content::-webkit-scrollbar-thumb {
  background-color: rgba(4, 12, 45, 0.2);
  border-radius: 10px;
  border: 4px transparent solid;
  background-clip: padding-box;
}

.modal-content::-webkit-scrollbar-thumb:hover {
  background-color: rgba(4, 12, 45, 0.4);
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  cursor: pointer;
  background-color: #F2F4F7;
  border-radius: 3000px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background-color: #E4E7EC;
}

.cross {
  padding: 8px;
}

.modal-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border: none;
  padding: 0 !important;
  gap: 2px;
  margin-bottom: 16px;
}

.modal-header-txt {
  font-family: euclid_semi_bold, sans-serif;
  color: #040C2D;
  font-size: 20px;
  font-style: normal;
  line-height: 24px;
}

.modal-header-description {
  font-family: euclid_medium, sans-serif;
  color: #667085;
  font-size: 14px;
  font-style: normal;
  line-height: 24px;
  margin-top: 6px;
}

/* Progress steps styling */
.progress-steps {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step {
  display: flex;
  gap: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  transition: all 0.3s ease;
  border-left: 3px solid #E4E7EC;
  background-color: #f5f5f5;
  align-items: center;
}

.step.active {
  background-color: rgba(56, 114, 224, 0.08);
  border-left: 3px solid #3872E0;
}

.step.completed {
  border-left: 3px solid #10B981;
}

.step.completed .step-icon {
  background-color: #10B981;
  color: white;
}

.step-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #E4E7EC;
  color: #667085;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.step-icon img {
  width: 16px;
  height: 16px;
}

.step.active .step-icon {
  background-color: #3872E0;
  color: white;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-title {
  font-family: euclid_semi_bold, sans-serif;
  color: #040C2D;
  font-size: 14px;
  line-height: 14px;
}

.step-description {
  font-family: euclid_regular, sans-serif;
  color: #667085;
  font-size: 12px;
}

.current-action {
  margin-top: 24px;
  padding: 12px 16px;
  background-color: #F9FAFB;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: euclid_medium, sans-serif;
  color: #344054;
  font-size: 14px;
}

.pulse-dot {
  width: 10px;
  height: 10px;
  background-color: #3872E0;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(56, 114, 224, 0.7);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(56, 114, 224, 0);
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(56, 114, 224, 0);
  }
}

/* Error message */
.error-message {
  margin-top: 24px;
  padding: 12px 16px;
  background-color: #FEF3F2;
  border: 1px solid #FDA29B;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: euclid_medium, sans-serif;
  color: #B42318;
  font-size: 14px;
}

.error-message i {
  font-size: 16px;
}

.retry-btn {
  margin-left: auto;
  background-color: #B42318;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-family: euclid_medium, sans-serif;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-btn:hover {
  background-color: #912018;
}

/* Transition animations */
.slide-fadeUp-enter-active,
.slide-fadeUp-leave-active {
  transition: all 0.3s ease;
}

.slide-fadeUp-enter-from,
.slide-fadeUp-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
